import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchContent, selectContent, selectContentLoading } from '../store/slices/contentSlice';
import { ContentItem as ContentItemType } from '../types/content';
import ContentItem from '../components/molecules/ContentItem';
import SearchBar from '../components/atoms/SearchBar';
import FilterTabs from '../components/molecules/FilterTabs';
import PageTemplate from '../components/templates/PageTemplate';
import { MobileHeaderType } from '../components/layout/ConfigurableMobileHeader';

const ContentContainer = styled.div`
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: ${({ theme }) => theme.sizes.header.height};
    padding-bottom: 64px; /* Space for bottom navigation */
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 960px;
  padding: 0 24px;
`;

const ContentHeader = styled.div`
  margin-bottom: 16px;
  width: 100%;
`;

const FilterTabsContainer = styled.div`
  margin-top: 16px;
  width: 100%;
  overflow-x: auto;
  
  /* Hide scrollbar but allow scrolling */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const ContentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const Content: React.FC = () => {
  const dispatch = useAppDispatch();
  const contentItems = useAppSelector(selectContent);
  const loading = useAppSelector(selectContentLoading);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  useEffect(() => {
    dispatch(fetchContent());
  }, [dispatch]);
  
  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter.toLowerCase());
  };
  
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };
  
  const filterTabs = [
    { id: 'all', label: 'All' },
    { id: 'live', label: 'Live' },
    { id: 'pending', label: 'Pending' },
    { id: 'revise', label: 'Revise' },
    { id: 'rejected', label: 'Rejected' }
  ];
  
  const filteredContent = contentItems ? contentItems.filter((item: ContentItemType) => {
    // Filter by status
    if (activeFilter !== 'all' && item.status.toLowerCase() !== activeFilter) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery && !item.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    return true;
  }) : [];
  
  return (
    <>
      {/* Use PageTemplate with title-only mobile header */}
      <PageTemplate
        hideHeaderOnMobile={true}
        mobileHeaderType={MobileHeaderType.TITLE_ONLY}
        mobileHeaderTitle="Content"
      >
        <ContentContainer>
          <ContentWrapper>
            <ContentHeader>
              <SearchBar 
                placeholder="search for content" 
                onSearch={handleSearch}
              />
              <FilterTabsContainer>
                <FilterTabs 
                  tabs={filterTabs} 
                  activeTab={activeFilter} 
                  onTabChange={handleFilterChange}
                />
              </FilterTabsContainer>
            </ContentHeader>
            
            <ContentList>
              {loading ? (
                <div>Loading content...</div>
              ) : filteredContent.length > 0 ? (
                filteredContent.map((item: ContentItemType) => (
                  <ContentItem key={item.id} content={item} />
                ))
              ) : (
                <div>No content found</div>
              )}
            </ContentList>
          </ContentWrapper>
        </ContentContainer>
      </PageTemplate>
    </>
  );
};

export default Content;
